/**
 * Generic task form used for "Other", "Medication Request", "Prescription
 * Request" and "Referral" types.
 *
 * They share the same fields (common + urgent); only the `data.type` sent
 * to the API differs.
 */

import { useId } from 'react'
import { z } from 'zod'

import type { CreatableTaskType } from '@snootsvet/shared'

import { useAppForm } from '@/frontend/components/form'
import { FieldWrapper } from '@/frontend/components/form/fields'
import { Button } from '@/frontend/components/ui/button'
import { Checkbox } from '@/frontend/components/ui/checkbox'
import { PatientSearch } from '@/frontend/features/patient/patient-search'

import { useCreateTask } from '../use-create-task'
import {
  hasAssignee,
  hasClinicWhenRequired,
  TASK_ASSIGNMENT_DEFAULTS,
  TaskAssignmentFieldGroup,
  taskAssignmentShape,
} from './task-assignment.field-group'

const otherFormSchema = z
  .object({
    patientId: z
      .string()
      .min(1, 'Please select a patient')
      .nullable()
      .refine(Boolean, 'Please select a patient'),
    title: z.string().min(1, 'Title is required'),
    urgent: z.boolean(),
    ...taskAssignmentShape,
  })
  .refine(hasAssignee, {
    message: 'Please select a team or staff member',
    path: ['assigneeRole'],
  })
  .refine(hasClinicWhenRequired, {
    message: 'Please select a clinic',
    path: ['assigneeClinicId'],
  })

type OtherFormValues = z.input<typeof otherFormSchema>

/** Wraps OtherTaskForm with a fixed `medication_request` type. */
export function MedicationRequestTaskForm(props: Omit<OtherTaskFormProps, 'taskType'>) {
  return <OtherTaskForm {...props} taskType="medication_request" />
}

/** Wraps OtherTaskForm with a fixed `prescription_request` type. */
export function PrescriptionRequestTaskForm(props: Omit<OtherTaskFormProps, 'taskType'>) {
  return <OtherTaskForm {...props} taskType="prescription_request" />
}

/** Wraps OtherTaskForm with a fixed `referral` type. */
export function ReferralTaskForm(props: Omit<OtherTaskFormProps, 'taskType'>) {
  return <OtherTaskForm {...props} taskType="referral" />
}

interface OtherTaskFormProps {
  /** Which task type to create — defaults to `'other'`. */
  taskType?: Exclude<CreatableTaskType, 'follow_up' | 'labs'>
  defaultValues?: { patientId?: string }
  onSuccess?: () => void
  onCancel?: () => void
}

export function OtherTaskForm(props: OtherTaskFormProps) {
  const taskType = props.taskType ?? 'other'
  const { mutateAsync: createTask } = useCreateTask()
  const urgentId = useId()

  const form = useAppForm({
    defaultValues: {
      patientId: null,
      title: '',
      ...TASK_ASSIGNMENT_DEFAULTS,
      urgent: false,
      ...props.defaultValues,
    } as OtherFormValues,
    validators: {
      onSubmit: otherFormSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      try {
        await createTask({
          title: value.title,
          patientId: value.patientId,
          data: { type: taskType, urgent: value.urgent },
          assigneeRole: value.assigneeRole,
          assigneeClinicId: value.assigneeClinicId,
          assigneeStaffId: value.assigneeStaffId,
        })
        form.reset()
        props.onSuccess?.()
      } catch (err) {
        formApi.setErrorMap({
          onSubmit: {
            form: err instanceof Error ? err.message : 'Failed to create task',
            fields: {},
          },
        })
      }
    },
  })

  const titleLabel =
    taskType === 'medication_request' || taskType === 'prescription_request'
      ? 'Reason for Request'
      : 'Title'

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <div className="flex flex-col gap-4 py-2">
          <form.GlobalError />

          {!props.defaultValues?.patientId && (
            <form.Field name="patientId">
              {(field) => (
                <FieldWrapper
                  field={field}
                  label="Patient"
                  required
                  render={({ id, disabled }) => (
                    <PatientSearch
                      id={id}
                      onChange={field.handleChange}
                      onBlur={field.handleBlur}
                      disabled={disabled}
                    />
                  )}
                />
              )}
            </form.Field>
          )}

          <form.AppField name="title">
            {(field) => (
              <field.InputField label={titleLabel} placeholder="Brief summary" required />
            )}
          </form.AppField>

          <form.Field name="urgent">
            {(field) => (
              <div className="flex items-center gap-2">
                <Checkbox
                  id={urgentId}
                  checked={field.state.value}
                  onCheckedChange={(checked) => field.handleChange(checked === true)}
                />
                <label htmlFor={urgentId} className="font-medium cursor-pointer">
                  Urgent
                </label>
              </div>
            )}
          </form.Field>

          <TaskAssignmentFieldGroup
            form={form}
            fields={{
              assigneeRole: 'assigneeRole',
              assigneeClinicId: 'assigneeClinicId',
              assigneeStaffId: 'assigneeStaffId',
            }}
          />
        </div>

        <form.Subscribe selector={(s) => [s.canSubmit, s.isSubmitting] as const}>
          {([canSubmit, isSubmitting]) => (
            <div className="flex items-center justify-end gap-2 pt-3">
              {props.onCancel && (
                <Button variant="outline" size="lg" type="button" onClick={props.onCancel}>
                  Cancel
                </Button>
              )}
              <Button type="submit" size="lg" disabled={!canSubmit || isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create Task'}
              </Button>
            </div>
          )}
        </form.Subscribe>
      </form.AppForm>
    </form>
  )
}
